/**
 * Peer marble ring — the pure logic core (no Three.js, headless-testable).
 *
 * A revolving set of NPC marbles rides the same centerline as the player so
 * the run always has company ahead and behind. The ring is a fixed set of
 * SLOTS keyed to signed arc-length offsets from the player; a slot spawns a
 * peer when it is open (session start, or after its previous peer despawned
 * and the slot's cooldown lapsed) and the peer despawns when it drifts out
 * of the live band (too far behind/ahead, or outside the built window).
 *
 * Each spawn draws a fresh identity + behaviour from
 * `rngFamily(seed, "peer/<slot>/<n>/<timeStamp>")` — the SPAWN TIME is part
 * of the seed material (user rule), so the ring is different every session
 * while generation itself stays a pure function of the seed. Peers are
 * kinematic centerline riders with per-peer speed character (base speed,
 * gravity response, drag) — the character drift is what revolves the ring:
 * fast peers pull away and despawn ahead, slow ones fall off the back.
 */
import { rngFamily } from "../gen/rng.ts";

/** Live band relative to the player: outside this, a peer despawns. */
export const BEHIND_LIMIT = 18;
export const AHEAD_LIMIT = 38;

/** Default ring size (overridable per session via ?peers=N). */
export const DEFAULT_PEER_COUNT = 6;

/**
 * Signed arc-length slot offsets for a ring of `n` peers: roughly a third
 * ride behind, the rest lead, spread evenly inside the live band with
 * margin so fresh spawns aren't born on the despawn edge.
 */
export function slotOffsets(n: number): number[] {
  const count = Math.max(0, Math.min(24, Math.floor(n)));
  if (count === 0) return [];
  const nBehind = count >= 3 ? Math.floor(count / 3) : count === 2 ? 1 : 0;
  const nAhead = count - nBehind;
  const out: number[] = [];
  const bLo = -BEHIND_LIMIT + 4;
  const bHi = -7;
  for (let k = 0; k < nBehind; k++) {
    out.push(
      Math.round(nBehind === 1 ? bHi : bLo + (bHi - bLo) * (k / (nBehind - 1))),
    );
  }
  const aLo = 6;
  const aHi = AHEAD_LIMIT - 6;
  for (let k = 0; k < nAhead; k++) {
    out.push(
      Math.round(nAhead === 1 ? aLo : aLo + (aHi - aLo) * (k / (nAhead - 1))),
    );
  }
  return out;
}

/** The default ring's offsets (kept as a const for tests/back-compat). */
export const SLOT_OFFSETS: readonly number[] = slotOffsets(DEFAULT_PEER_COUNT);

/** Seconds of spawn scale-in / despawn scale-out. */
export const IN_TIME = 0.45;
export const OUT_TIME = 0.35;

/** Peer marble mesh variants (weights): mostly the classic ball, with the
 * kit's other marbles as rarer identities. */
const MESH_CHOICES: readonly (readonly [string, number])[] = [
  ["marble-center-high", 4],
  ["marble-center-low", 2],
  ["marble-high", 1.5],
  ["marble-low", 1],
  ["marble-center-butterfly-high", 0.4],
];

export interface PeerIdentity {
  meshId: string;
  /** Material tint 0–255, or null for the stock kit marble. */
  tint: readonly [number, number, number] | null;
  /** Mesh scale relative to the player marble's scale. */
  scale: number;
  /** Signed lateral offset from the centerline (world units). */
  lateral: number;
  wobbleHz: number;
  wobbleAmp: number;
  wobblePhase: number;
}

export interface PeerBehaviour {
  base: number;
  min: number;
  max: number;
  gravity: number;
  drag: number;
}

export interface Peer {
  slot: number;
  /** RNG label path — doubles as a stable debug id. */
  label: string;
  s: number;
  speed: number;
  age: number;
  phase: "in" | "live" | "out";
  phaseT: number;
  identity: PeerIdentity;
  behaviour: PeerBehaviour;
}

/** Player-feel constants the peers riff on (endless kinematic law). */
const BASE_SPEED = 2.2;
const MIN_SPEED = 1.2;
const MAX_SPEED = 7;
const GRAVITY = 6;
const DRAG = 0.9;

export interface RingEvents {
  spawned: Peer[];
  removed: Peer[];
}

export class PeerRingCore {
  /** One entry per slot; null = open. */
  readonly peers: (Peer | null)[];
  readonly offsets: readonly number[];
  private readonly cooldowns: number[];
  private readonly counters: number[];

  constructor(
    private readonly seed: string,
    offsets: readonly number[] = SLOT_OFFSETS,
  ) {
    this.offsets = offsets;
    this.peers = offsets.map(() => null);
    this.counters = offsets.map(() => 0);
    // Staggered opening: slots come alive over the first moments instead
    // of the whole ring popping in on one frame.
    this.cooldowns = offsets.map((_, i) =>
      rngFamily(seed, `peer-open/${i}`).float() * 2
    );
  }

  get liveCount(): number {
    return this.peers.filter((p) => p !== null).length;
  }

  /**
   * Advance the ring one frame.
   * `band` is the arc-length range currently built (sampleable); `nowMs`
   * seeds new spawns (wall clock — presentation-only nondeterminism, by
   * design); `tanYAt` samples the centerline tangent's y at an arc length
   * (null outside the built window).
   */
  update(
    dt: number,
    playerS: number,
    band: readonly [number, number],
    nowMs: number,
    tanYAt: (s: number) => number | null,
  ): RingEvents {
    const events: RingEvents = { spawned: [], removed: [] };

    for (let slot = 0; slot < this.offsets.length; slot++) {
      const peer = this.peers[slot];
      if (!peer) {
        this.cooldowns[slot] -= dt;
        if (this.cooldowns[slot] <= 0) {
          const spawned = this.spawn(slot, playerS, band, nowMs);
          if (spawned) {
            this.peers[slot] = spawned;
            events.spawned.push(spawned);
          } else {
            this.cooldowns[slot] = 0.5; // band too tight — retry shortly
          }
        }
        continue;
      }

      peer.age += dt;
      peer.phaseT += dt;
      if (peer.phase === "in" && peer.phaseT >= IN_TIME) {
        peer.phase = "live";
        peer.phaseT = 0;
      }
      if (peer.phase === "out") {
        if (peer.phaseT >= OUT_TIME) {
          this.peers[slot] = null;
          this.cooldowns[slot] = 1 +
            rngFamily(this.seed, `${peer.label}/cooldown`).float() * 2.5;
          events.removed.push(peer);
        }
        continue; // frozen in place while scaling out
      }

      // Kinematic speed law with the peer's own character.
      const b = peer.behaviour;
      const ty = tanYAt(peer.s) ?? 0;
      peer.speed += (-ty * b.gravity - b.drag * (peer.speed - b.base)) * dt;
      peer.speed = Math.min(b.max, Math.max(b.min, peer.speed));
      peer.s += peer.speed * dt;

      // Out of the live band → begin the scale-out.
      const rel = peer.s - playerS;
      if (
        rel < -BEHIND_LIMIT || rel > AHEAD_LIMIT ||
        peer.s < band[0] + 0.5 || peer.s > band[1] - 0.5
      ) {
        peer.phase = "out";
        peer.phaseT = 0;
      }
    }
    return events;
  }

  /** Immediately release every peer (session teardown). */
  clear(): Peer[] {
    const out: Peer[] = [];
    for (let i = 0; i < this.peers.length; i++) {
      const p = this.peers[i];
      if (p) out.push(p);
      this.peers[i] = null;
    }
    return out;
  }

  private spawn(
    slot: number,
    playerS: number,
    band: readonly [number, number],
    nowMs: number,
  ): Peer | null {
    const lo = band[0] + 1;
    const hi = band[1] - 1;
    if (hi - lo < 4) return null;
    let s = playerS + this.offsets[slot];
    s = Math.min(hi, Math.max(lo, s));
    // Never materialize on top of the player.
    if (Math.abs(s - playerS) < 3) {
      s = playerS + (this.offsets[slot] >= 0 ? 3 : -3);
      if (s < lo || s > hi) return null;
    }

    const n = this.counters[slot]++;
    // Wall-clock stamp (quarter-second buckets) is deliberately part of
    // the seed material: WHEN a peer spawns shapes WHO it is.
    const label = `peer/${slot}/${n}/${Math.floor(nowMs / 250)}`;
    const rng = rngFamily(this.seed, label);

    const identity: PeerIdentity = {
      meshId: rng.weighted(MESH_CHOICES),
      tint: rng.chance(0.25)
        ? null
        : drawTint(rng.float(), rng.float(), rng.float()),
      scale: 0.8 + rng.float() * 0.5,
      lateral: (rng.chance(0.5) ? 1 : -1) * (0.06 + rng.float() * 0.18),
      wobbleHz: 0.15 + rng.float() * 0.35,
      wobbleAmp: 0.03 + rng.float() * 0.07,
      wobblePhase: rng.float() * Math.PI * 2,
    };
    const behaviour: PeerBehaviour = {
      base: BASE_SPEED * (0.82 + rng.float() * 0.46),
      min: MIN_SPEED * (0.8 + rng.float() * 0.3),
      max: MAX_SPEED * (0.85 + rng.float() * 0.35),
      gravity: GRAVITY * (0.8 + rng.float() * 0.55),
      drag: DRAG * (0.7 + rng.float() * 0.45),
    };
    return {
      slot,
      label,
      s,
      speed: behaviour.base,
      age: 0,
      phase: "in",
      phaseT: 0,
      identity,
      behaviour,
    };
  }
}

/** Marble phenotype (user round): the outer SHELL tint and an EFFECT are
 * two additional MULTIPLICATIVE expressions layered over the base look —
 * shell multiplies the color, the effect multiplies material character
 * (gloss/pearl/ember/ghost at a drawn intensity). */
export interface RacerLook {
  meshId: string;
  tint: readonly [number, number, number] | null;
  /** Per-channel color multiplier (1,1,1 = no shell). */
  shell: readonly [number, number, number];
  effect: {
    kind: "none" | "gloss" | "pearl" | "ember" | "ghost";
    intensity: number;
  };
}

/** Gentle multiplicative shell color: identity at strength 0; a cosine
 * palette keeps every channel in (1−s, 1]. */
function shellOf(
  h: number,
  strength: number,
): readonly [number, number, number] {
  const ch = (o: number): number =>
    1 - strength * (0.5 + 0.5 * Math.cos(2 * Math.PI * (h + o)));
  return [ch(0), ch(1 / 3), ch(2 / 3)];
}

/** Seed-stable looks for a REAL racer marble (M15 — physics peers). Uses
 * the same mesh pool + tint law as ring peers; label-derived, so adding
 * racers never perturbs anything else. Phenotype draws are APPENDED so
 * older identities stay byte-identical. */
export function racerIdentity(seed: string, index: number): RacerLook {
  const rng = rngFamily(seed, `racer/${index}`);
  const meshId = rng.weighted(MESH_CHOICES);
  const tint = rng.chance(0.2)
    ? null
    : drawTint(rng.float(), rng.float(), rng.float());
  const shell = shellOf(rng.float(), rng.float() * 0.35);
  const kind = rng.weighted(
    [
      ["none", 0.35],
      ["gloss", 0.2],
      ["pearl", 0.15],
      ["ember", 0.15],
      ["ghost", 0.15],
    ] as const,
  ) as RacerLook["effect"]["kind"];
  const intensity = 0.4 + rng.float() * 0.6;
  return { meshId, tint, shell, effect: { kind, intensity } };
}

// --- rivalries (M20b) -------------------------------------------------------

export interface Rival {
  /** The racer this one is fixated on. */
  target: number;
  /** Grudge = hunt them; ally = spare them (and spread on braids). */
  valence: "grudge" | "ally";
  /** 0.5-1: scales every expression lever. */
  intensity: number;
}

/**
 * Seeded pairwise relationship for racer `i` in a pack of `n` — DIRECTED
 * (i fixates on target; the target is oblivious). Symmetry would need a
 * canonical pair hash, and shared hashes are banned by the rng law —
 * asymmetric grudges are both compliant and better drama. Own label
 * `rival/${i}`: a fresh per-racer stream, so racer i's relationship is
 * invariant under everything about racer j. Draw order is FIXED — any
 * future rival trait appends after intensity.
 */
export function rivalFor(seed: string, i: number, n: number): Rival | null {
  if (n < 2) return null;
  const rng = rngFamily(seed, `rival/${i}`);
  const has = rng.chance(0.5);
  const d = 1 + rng.int(0, 16); // drawn even when unused: fixed order
  const valence = rng.chance(0.7) ? "grudge" as const : "ally" as const;
  const intensity = 0.5 + rng.float() * 0.5;
  if (!has) return null;
  const target = (i + d) % n;
  if (target === i) return null;
  return { target, valence, intensity };
}

/** Rich marble-glass tints (bounded like the pipe colors so peers read
 * against every atmosphere). */
function drawTint(
  h: number,
  sIn: number,
  lIn: number,
): readonly [number, number, number] {
  const s = 0.45 + sIn * 0.4;
  const l = 0.42 + lIn * 0.24;
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [
    Math.round(channel(h + 1 / 3) * 255),
    Math.round(channel(h) * 255),
    Math.round(channel(h - 1 / 3) * 255),
  ];
}
